import { useSyncExternalStore } from "react";

// Rotates through reassuring copy while the AI builds a project plan.
// Three phases (~2.5s each) so the overlay always feels like progress, even
// when the actual AI call is fast.
const PHASES = [
  {
    headline: "Creating your plan…",
    sub: "Breaking your project into manageable steps.",
  },
  {
    headline: "Building your roadmap…",
    sub: "Finding materials, creating steps and checking for anything important.",
  },
  {
    headline: "Working out the Half Job…",
    sub: "Turning your idea into something you can actually finish.",
  },
];

// Module-level rotating clock — React subscribes via useSyncExternalStore,
// which is the canonical pattern for time-based external state and keeps
// the strict "no setState in effect" lint rule happy.
let _tick = 0;
const _listeners = new Set();
let _interval = null;
function _subscribe(cb) {
  _listeners.add(cb);
  if (_interval === null) {
    _interval = setInterval(() => {
      _tick = (_tick + 1) % PHASES.length;
      _listeners.forEach((fn) => fn());
    }, 2500);
  }
  return () => {
    _listeners.delete(cb);
    if (_listeners.size === 0 && _interval !== null) {
      clearInterval(_interval);
      _interval = null;
      _tick = 0;
    }
  };
}
const _getSnapshot = () => _tick;
const _getServerSnapshot = () => 0;

export default function AILoadingOverlay({ open, testidBase = "ai-loading", onCancel }) {
  // Subscribe to the rotating clock at the top level (rules of hooks).
  const tick = useSyncExternalStore(_subscribe, _getSnapshot, _getServerSnapshot);
  if (!open) return null;
  const phase = PHASES[tick];

  return (
    <div
      data-testid={testidBase}
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] flex items-center justify-center px-6"
      style={{
        background: "rgba(58, 46, 42, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div className="bg-chalk border-2 border-stone_border rounded-2xl p-7 shadow-2xl max-w-sm w-full text-center">
        <div className="mx-auto mb-5 relative w-20 h-20">
          <div className="absolute inset-0 rounded-2xl bg-timber-900 flex items-center justify-center">
            <img
              src="/logo.svg"
              alt=""
              className="w-14 h-14 animate-pulse"
              draggable={false}
            />
          </div>
        </div>
        <p
          data-testid={`${testidBase}-headline`}
          className="font-heading text-2xl font-bold text-timber-900 leading-tight"
        >
          {phase.headline}
        </p>
        <p
          data-testid={`${testidBase}-sub`}
          className="text-sm text-timber-700 mt-2"
        >
          {phase.sub}
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-5">
          <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "240ms" }} />
        </div>
        <p className="text-[11px] text-timber-500 mt-4 uppercase tracking-wider font-bold">
          Usually 10–25 seconds
        </p>
        {onCancel && (
          <button
            type="button"
            data-testid={`${testidBase}-cancel`}
            onClick={onCancel}
            className="mt-5 text-sm font-bold text-timber-700 underline underline-offset-4 hover:text-timber-900"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
